import { useEffect, useRef, useState } from "react";
import { Phone, MessageSquare, Send } from "lucide-react";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import { Button } from "@/components/ui/button";
import { useHasInteracted } from "@/hooks/use-interaction";

interface FormEmbedProps {
  id?: string;
  className?: string;
}

/**
 * Renders the LeadConnector form widget when a form ID has been issued for
 * this account. Until then it falls back to a built-in estimate request form
 * so no lead is ever dropped or routed to the wrong inbox.
 *
 * The third-party iframe + form_embed.js (Cloudflare cookies, reCAPTCHA, phone
 * libraries — several MB) are loaded on the visitor's first interaction rather
 * than during initial page load. A height-reserved placeholder keeps the layout
 * stable and always offers immediate call/text contact.
 */
const FormEmbed = ({ id = "inline-quote-form", className = "" }: FormEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const embedSrc = siteConfig.widgets.formIframeSrc;
  const ready = useHasInteracted();

  useEffect(() => {
    if (!embedSrc || !ready) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [embedSrc, ready]);

  if (!embedSrc) {
    return <FallbackQuoteForm className={className} />;
  }

  if (!ready) {
    return (
      <div className={className}>
        <div
          className="flex flex-col items-center justify-center gap-4 text-center rounded-[10px] bg-muted/40 px-6"
          style={{ minHeight: siteConfig.widgets.formHeight }}
        >
          <div
            className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin"
            aria-hidden="true"
          />
          <p className="text-muted-foreground text-sm max-w-xs">
            Loading the secure estimate form. Prefer to reach us now?
          </p>
          <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
            <Button asChild variant="outline" className="flex-1 w-full gap-2 rounded-xl font-semibold">
              <a href={siteConfig.phoneHref}>
                <Phone className="h-4 w-4" /> Call
              </a>
            </Button>
            <Button asChild variant="outline" className="flex-1 w-full gap-2 rounded-xl font-semibold">
              <a href={siteConfig.smsHref}>
                <MessageSquare className="h-4 w-4" /> Text
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      <iframe
        src={embedSrc}
        style={{ width: "100%", border: "none", borderRadius: 10, display: "block" }}
        id={id}
        data-layout="{'id':'INLINE'}"
        data-trigger-type="alwaysShow"
        data-activation-type="alwaysActivated"
        data-deactivation-type="neverDeactivate"
        data-form-name={siteConfig.widgets.formName}
        data-height={siteConfig.widgets.formHeight}
        data-layout-iframe-id={id}
        data-form-id={siteConfig.widgets.formId ?? undefined}
        title={siteConfig.widgets.formName}
        scrolling="no"
      />
    </div>
  );
};

const FallbackQuoteForm = ({ className = "" }: { className?: string }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [service, setService] = useState(services[0].title);
  const [details, setDetails] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body = [
      `Name: ${name}`,
      `Phone: ${phone}`,
      `City: ${city}`,
      `Service needed: ${service}`,
      "",
      "Project details:",
      details,
    ].join("\n");

    window.location.href = `mailto:${siteConfig.email}?subject=${encodeURIComponent(
      `Free estimate request — ${service}`
    )}&body=${encodeURIComponent(body)}`;
  };

  const field =
    "w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary";

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="qf-name" className="sr-only">
            Your name
          </label>
          <input
            id="qf-name"
            className={field}
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="qf-phone" className="sr-only">
            Phone number
          </label>
          <input
            id="qf-phone"
            type="tel"
            className={field}
            placeholder="Phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor="qf-city" className="sr-only">
          City
        </label>
        <input
          id="qf-city"
          className={field}
          placeholder="City (e.g. Highlands Ranch)"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="qf-service" className="sr-only">
          Service needed
        </label>
        <select
          id="qf-service"
          className={field}
          value={service}
          onChange={(e) => setService(e.target.value)}
        >
          {services.map((s) => (
            <option key={s.slug} value={s.title}>
              {s.title} — {s.titleEs}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="qf-details" className="sr-only">
          Project details
        </label>
        <textarea
          id="qf-details"
          className={`${field} min-h-[96px] resize-y`}
          placeholder="Tell us about the project — rooms, square footage, timeline…"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
      </div>

      <Button type="submit" size="lg" className="w-full gap-2 rounded-xl font-semibold">
        <Send className="h-4 w-4" /> Request My Free Estimate
      </Button>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button asChild type="button" variant="outline" className="w-full gap-2 rounded-xl font-semibold">
          <a href={siteConfig.phoneHref}>
            <Phone className="h-4 w-4" /> Call
          </a>
        </Button>
        <Button asChild type="button" variant="outline" className="w-full gap-2 rounded-xl font-semibold">
          <a href={siteConfig.smsHref}>
            <MessageSquare className="h-4 w-4" /> Text
          </a>
        </Button>
      </div>

      <p className="text-muted-foreground text-xs text-center pt-1">
        Se habla español · Free written estimates · No obligation
      </p>
    </form>
  );
};

export default FormEmbed;
